import { UpdateCart } from '@diamond/shared/types';
import { patchCart } from '@diamond/sol-buyer-context';
import { useAuthStore } from '@diamond/sol-buyer-context';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { patchCartItemInCartQueries } from './cartQueryCache';

export interface UseUpdateCartItemArgs {
  itemId: string;
  onError?: (errorMessage: string) => void;
}

export interface UseUpdateCartItemReturn {
  handleUpdateQuantity: (quantity: number, note: string) => void;
  isUpdating: boolean;
}

/**
 * Hook for updating cart item quantity via API.
 *
 * Fires API calls immediately to persist quantity changes to the server.
 * Debouncing is handled at the UI level (useStockNumberInput) to avoid duplicate calls.
 * We also update any cached `['Cart', ...]` queries on success to prevent UI drift,
 * because cart list queries use a long `staleTime` and otherwise can remain stale
 * until a manual refetch.
 *
 * Quantity Management Flow:
 * - User changes quantity → Debounced at UI level (1 second delay)
 * - After debounce → API call fires immediately
 * - API success → Backend syncs cart automatically
 * - Cache is patched locally immediately to keep UI in sync
 * - Store sync happens separately on checkbox selection (reads from React Query cache)
 *
 * Why We Patch Cache (no refetch):
 * - Avoids unnecessary refetch after every quantity change
 * - Prevents stale `item.note` / `item.quantity` when `staleTime` is long
 * - Still keeps server as the source of truth (next GET will confirm)
 *
 * @param args - Configuration object
 * @param args.itemId - The cart item ID to update
 *
 * @returns Object with handleUpdateQuantity function and isUpdating state
 *
 * @example
 * ```tsx
 * const { handleUpdateQuantity, isUpdating } = useUpdateCartItem({
 *   itemId: 'cart-123'
 * });
 *
 * // Update quantity (fires immediately, debouncing handled upstream)
 * handleUpdateQuantity(5000, ''); // 5000 = 5 units (magnified)
 * ```
 */
export const useUpdateCartItem = ({
  itemId,
  onError,
}: UseUpdateCartItemArgs): UseUpdateCartItemReturn => {
  const token = useAuthStore((state) => state.access_token);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (params: UpdateCart) => patchCart(token, itemId, params),
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || 'Gagal memperbarui keranjang';
      onError?.(message);
    },
    onSuccess: (_data, variables) => {
      patchCartItemInCartQueries(queryClient, itemId, {
        quantity: variables.quantity,
        note: variables.note,
      });
    },
  });

  const handleUpdateQuantity = useCallback(
    (quantity: number, note: string) => {
      const updatePayload: UpdateCart = {
        quantity,
        note: note || null,
      };

      if (
        quantity > 0 ||
        (note !== null && note !== undefined && note !== '')
      ) {
        mutate(updatePayload);
      }
    },
    [mutate]
  );

  return {
    handleUpdateQuantity,
    isUpdating: isPending,
  };
};
